import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ThresholdSlider } from '@/components/cameras/detection/ThresholdSlider'
import { useGlobalThresholds } from '@/components/detection-accuracy/useGlobalThresholds'
import { cn } from '@/lib/utils'
import { useDirectionStore } from '@/store/directionStore'
import type { GlobalThresholdModel } from '@/types'

function ModelBlock({ model, onCommit, baseDefault }: {
  model: GlobalThresholdModel
  baseDefault: number
  onCommit: (classId: number, conf: number | null) => void
}) {
  const { t } = useTranslation('detectionAccuracy')
  const d = useDirectionStore((s) => s.direction)
  const [openModel, setOpenModel] = useState(true)
  return (
    <div className={cn('border border-border',
      d === 'current' && 'rounded-lg',
      d === 'command' && 'rounded',
      d === 'saas' && 'rounded',
      d === 'expressive' && 'rounded-xl',
    )}>
      <button type="button" onClick={() => setOpenModel((v) => !v)}
        className={cn('flex w-full items-center justify-between text-left',
          d === 'current' && 'px-4 py-3',
          d === 'command' && 'px-3 py-2',
          d === 'saas' && 'px-4 py-2',
          d === 'expressive' && 'px-4 py-3',
        )}>
        <span className={cn('font-medium', d === 'expressive' && 'font-display')}>{model.ai_model_name}</span>
        <span className={cn('text-xs text-muted-foreground', d === 'command' && 'font-mono text-[11px]')}>
          {model.classes.length} {t('classes')} · {t('base')} {Math.round(baseDefault * 100)}%
        </span>
      </button>
      {openModel && (
        <div className={cn('space-y-3 border-t border-border',
          d === 'current' && 'px-4 py-3',
          d === 'command' && 'px-3 py-2',
          d === 'saas' && 'px-4 py-3',
          d === 'expressive' && 'px-4 py-3',
        )}>
          {model.classes.map((c) => {
            const inherit = c.global_confidence === null
            const value = c.global_confidence ?? baseDefault
            return (
              <div key={c.class_id} className="grid grid-cols-[10rem_1fr_auto] items-center gap-3">
                <span className={cn('truncate text-sm', d === 'command' && 'font-mono text-[12px]')} title={c.class_name}>{c.class_name}</span>
                <ThresholdSlider
                  value={value}
                  ariaLabel={`${model.ai_model_name} ${c.class_name}`}
                  onCommit={(v) => onCommit(c.class_id, v)}
                />
                <button type="button"
                  disabled={inherit}
                  onClick={() => onCommit(c.class_id, null)}
                  className={cn('text-xs text-primary underline disabled:opacity-40',
                    d === 'command' && 'font-mono text-[10px]',
                  )}>
                  {inherit ? `${t('base')} ${Math.round(baseDefault * 100)}%` : t('reset')}
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export function DetectionAccuracyPage() {
  const { t } = useTranslation('detectionAccuracy')
  const d = useDirectionStore((s) => s.direction)
  const g = useGlobalThresholds()

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn('text-lg font-semibold', d === 'expressive' && 'font-display')}>{t('title')}</h1>
          <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
        </div>
        <button type="button"
          onClick={() => { if (window.confirm(t('resetAllConfirm'))) g.resetCameras() }}
          disabled={g.resetting}
          className="rounded-md border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50">
          {t('resetAll')}
        </button>
      </div>

      <div className="flex h-5 items-center text-xs text-muted-foreground">
        {g.saving
          ? t('saving')
          : g.saveError
            ? (
                // current: amber-600; command/saas: primary; expressive: amber-600
                <span className={cn(
                  d === 'current' && 'text-amber-600',
                  d === 'command' && 'text-primary font-mono text-[11px]',
                  d === 'saas' && 'text-primary',
                  d === 'expressive' && 'text-amber-600',
                )}>
                  {t('saveRetry')}
                </span>
              )
            : t('saved')}
      </div>

      {g.isLoading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('loading')}
        </div>
      ) : g.isError ? (
        <div className="flex items-center gap-3 py-6 text-sm text-muted-foreground">
          <span>{t('loadError')}</span>
          <button type="button" onClick={() => g.refetch()} className="text-primary underline">{t('retry')}</button>
        </div>
      ) : g.models.length === 0 ? (
        <p className="py-6 text-sm text-muted-foreground">{t('noModels')}</p>
      ) : (
        <div className="space-y-3">
          {g.models.map((m) => (
            <ModelBlock key={m.ai_model_id} model={m} baseDefault={m.base_default}
              onCommit={(class_id, confidence) =>
                g.update({ base_model_id: m.base_model_id, class_id, confidence })} />
          ))}
        </div>
      )}
    </div>
  )
}

export default DetectionAccuracyPage
